import { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { BookmarksOutlined, DeleteOutline } from '@mui/icons-material';
import type { SavedSearch } from '../utils/savedSearchesApi';

interface SavedSearchesMenuProps {
  savedSearches: SavedSearch[];
  loading: boolean;
  error?: string | null;
  onLoad: (search: SavedSearch) => void;
  onDelete: (searchId: string) => void;
}

export const SavedSearchesMenu = ({
  savedSearches,
  loading,
  error,
  onLoad,
  onDelete,
}: SavedSearchesMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoad = (search: SavedSearch) => {
    onLoad(search);
    handleClose();
  };

  const handleDelete = (e: React.MouseEvent, searchId: string) => {
    e.stopPropagation();
    onDelete(searchId);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Saved Searches">
        <IconButton
          onClick={handleOpen}
          aria-label="Saved Searches"
          sx={{ flexShrink: 0 }}
        >
          <BookmarksOutlined />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { width: 320, maxHeight: 400 } },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Saved Searches
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              Failed to load saved searches
            </Typography>
          </Box>
        ) : savedSearches.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No saved searches yet
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0 }}>
            {savedSearches.map((search) => (
              <ListItemButton
                key={search.searchId}
                onClick={() => handleLoad(search)}
                sx={{ pr: 6 }}
              >
                <ListItemText
                  primary={search.name}
                  secondary={search.searchParams.q || 'Advanced filters'}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label={`Delete saved search: ${search.name}`}
                    size="small"
                    onClick={(e) => handleDelete(e, search.searchId)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};
